/* eslint-disable no-console */

import { register } from "register-service-worker";

let refreshing = false;
let registrationRef: ServiceWorkerRegistration | null = null;
let updateCheck: Promise<void> | null = null;
let wasHidden = false;
const LAST_REFRESHED_SHELL = "oneword-last-refreshed-shell";

const isSafeToRefresh = () => {
  const path = window.location.pathname;
  return path === "/" ||
    path === "/play/prepare" ||
    path === "/play/create" ||
    path === "/play/join" ||
    path === "/rooms" ||
    path === "/privacy" ||
    path === "/static/rules" ||
    /^\/[0-9a-z]{4}\/?$/i.test(path);
};

const shellFingerprint = (document: Document) => [
  ...Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))
    .map(node => new URL(node.src, window.location.origin).pathname),
  ...Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
    .map(node => new URL(node.href, window.location.origin).pathname),
].sort().join("|");

const newApplicationShellFingerprint = async () => {
  const response = await fetch(
    `${process.env.BASE_URL}index.html?app-update=${Date.now()}`,
    { cache: "no-store" }
  );
  if (!response.ok) return "";

  const latestDocument = new DOMParser().parseFromString(await response.text(), "text/html");
  const currentFingerprint = shellFingerprint(document);
  const latestFingerprint = shellFingerprint(latestDocument);
  if (!currentFingerprint || !latestFingerprint || currentFingerprint === latestFingerprint) {
    try {
      sessionStorage.removeItem(LAST_REFRESHED_SHELL);
    }
    catch {
      // Storage can be unavailable in restricted Safari modes.
    }
    return "";
  }
  return latestFingerprint;
};

const markShellForSingleRefresh = (fingerprint: string) => {
  try {
    if (sessionStorage.getItem(LAST_REFRESHED_SHELL) === fingerprint) return false;
    sessionStorage.setItem(LAST_REFRESHED_SHELL, fingerprint);
    return true;
  }
  catch {
    // Never risk a reload loop when Safari storage is unavailable.
    return false;
  }
};

const refreshIfUpdated = async () => {
  if (!registrationRef || refreshing || !isSafeToRefresh() || updateCheck) return;

  updateCheck = (async () => {
    try {
      await registrationRef?.update();

      if (registrationRef?.waiting) {
        registrationRef.waiting.postMessage({ type: "SKIP_WAITING" });
        return;
      }

      // iOS can restore an old standalone page after the new worker has
      // already activated, so controllerchange is no longer emitted to it.
      // Compare the live application shell with an uncached production copy.
      const latestFingerprint = await newApplicationShellFingerprint();
      if (latestFingerprint && markShellForSingleRefresh(latestFingerprint)) {
        refreshing = true;
        window.location.reload();
      }
    }
    catch (error) {
      // Offline launch remains fully usable from the existing worker cache.
      console.log("Unable to check for an app update right now.", error);
    }
    finally {
      updateCheck = null;
    }
  })();

  await updateCheck;
};

if (process.env.NODE_ENV === "production" && process.env.VUE_APP_NATIVE !== "true") {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing || !isSafeToRefresh()) return;
    refreshing = true;
    window.location.reload();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      wasHidden = true;
      return;
    }
    if (wasHidden) {
      wasHidden = false;
      void refreshIfUpdated();
    }
  });

  window.addEventListener("pageshow", event => {
    if (event.persisted) void refreshIfUpdated();
  });

  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      console.log(
        "App is being served from cache by a service worker.\n" +
          "For more details, visit https://goo.gl/AFskqB"
      );
    },
    registered(registration) {
      console.log("Service worker has been registered.");
      registrationRef = registration;

      // Check on every real launch instead of waiting for Safari's service-
      // worker update interval. Foreground restores are handled above too.
      void refreshIfUpdated();
    },
    cached() {
      console.log("Content has been cached for offline use.");
    },
    updatefound() {
      console.log("New content is downloading.");
    },
    updated(registration) {
      // Lobby and room pages are recoverable, so activate the new client there
      // immediately. Never reload in the middle of a game or an offline run.
      if (registration.waiting && isSafeToRefresh()) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
      else {
        console.log("New content is ready for the next safe app screen.");
      }
    },
    offline() {
      console.log(
        "No internet connection found. App is running in offline mode."
      );
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    },
  });
}
